// TranslationRow.component.tsx
import React, { useState, useEffect, useCallback, memo } from "react"
import { Input } from "antd"
import { Translation } from "types/questionnaire.types"

const { TextArea } = Input

/**
 * Sanitizes HTML to only allow safe formatting tags and handles <p> tags
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string
 */
const sanitizeHtml = (html: string): string => {
  if (!html) return ""
  // Remove <p> tags but keep content, replace </p> with <br> for line breaks
  let result = html.replace(/<p>/gi, "").replace(/<\/p>/gi, "<br>")
  // Remove trailing <br>
  result = result.replace(/<br>$/i, "")
  // Only allow specific safe tags
  const allowedTags = ["b", "i", "u", "br", "strong", "em"]
  const tagPattern = new RegExp(
    `<(?!/?(?:${allowedTags.join("|")})\\b)[^>]*>`,
    "gi"
  )
  return result.replace(tagPattern, "")
}

/**
 * Props interface for the TranslationRow component
 */
export interface TranslationRowProps {
  item: Translation
  selectedLanguage: string
  masterWidth: number
  translationWidth: number
  onTranslationChange: (identifier: string, newValue: string) => void
}

/**
 * Memoized row component that manages its own local state for the input
 * This prevents the entire table from re-rendering on every keystroke
 */
const TranslationRow = memo(
  ({
    item,
    selectedLanguage,
    masterWidth,
    translationWidth,
    onTranslationChange,
  }: TranslationRowProps) => {
    const [localValue, setLocalValue] = useState(item[selectedLanguage] || "")

    // Sync local value when selectedLanguage or item changes from parent
    useEffect(() => {
      setLocalValue(item[selectedLanguage] || "")
    }, [item, selectedLanguage])

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLocalValue(e.target.value)
      },
      []
    )

    const handleBlur = useCallback(() => {
      // Only update parent if value actually changed
      if (localValue !== (item[selectedLanguage] || "")) {
        onTranslationChange(item.identifier, localValue)
      }
    }, [localValue, item, selectedLanguage, onTranslationChange])

    return (
      <tr>
        <td
          className="p-2 border"
          style={{ width: masterWidth }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(item.MASTER),
          }}
        />
        <td className="p-2 border" style={{ width: translationWidth }}>
          <TextArea
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            autoSize={{ minRows: 1, maxRows: 6 }}
            className="border-none"
            style={{ width: "100%" }}
          />
        </td>
      </tr>
    )
  }
)

export default TranslationRow
